<template>
  <b-row class="my-5">
    <vue-element-loading
      class="behind_modal_z_index"
      :active="has_payment_intention"
      color="#ffffff"
      background-color="rgba(0, 0, 0, .7)"
      :text="$t('checkout_control.payment_in_progress')"
    />
    <b-col cols="12">
      <b-alert
        show
        v-if="is_order_payed_and_with_webpay"
        variant="success"
      >
        <div class="pb-2">
          <strong>
            {{ $t('checkout_control.webpay_payment_confirm') }}
          </strong>
        </div>

        <i18n path="checkout_control.webpay_payment_auth_info" tag="div">
          <span place="auth_code">
            <strong>
              {{ order_auth_code }}
            </strong>
          </span>
        </i18n>
        <i18n path="checkout_control.webpay_payment_date" tag="div">
          <span place="payment_date">
            <strong>
              {{ $formatDate(order_payment_date) }}
            </strong>
          </span>
        </i18n>
        <i18n path="checkout_control.webpay_payment_reference" tag="div">
          <span place="payment_reference">
            <strong>
              {{ order_payment_intention_uuid }}
            </strong>
          </span>
        </i18n>
        <i18n path="checkout_control.webpay_payment_amount" tag="div">
          <span place="payment_amount">
            <strong>
              {{ order_payment_amount }}
            </strong>
          </span>
        </i18n>
      </b-alert>
    </b-col>
    <b-col cols="12" md="3" v-if="purchase_options.pharmacy.logo">
      <b-card footer-tag="footer">
        <div class="imgcontainer mb-0" style="--aspect-ratio:1/1;">
          <PictureFallback
            :src="purchase_options.pharmacy.logo"
            type="pharmacy"
            alt="Pharmacy Logo"
            class="card-img p-2"
          />
        </div>
        <p
          v-if="purchase_options.pharmacy.public_name"
          class="text-center align-middle m-0 text-truncate"
          slot="footer"
        >
          {{ purchase_options.pharmacy.public_name }}
        </p>
        <p
          v-else
          class="text-center align-middle m-0 text-truncate"
          slot="footer"
        >
          {{ purchase_options.pharmacy.legal_name }}
        </p>
      </b-card>
    </b-col>
    <b-col cols="12" md="9">
      <div>
        <vue-element-loading
          :active="order_finish_loading"
          color="#FF6700"
          :text="$t('checkout_control.please_wait')"
        />

        <div class="summary-title mt-4 mt-md-0">
          {{ $t("checkout_control.sub_summary_title") }}
        </div>
        <b-container fluid class="pl-0">
          <b-row>
            <b-col cols="12" md="9">
              <b-row
                v-for="(drug, index) in transaction.item_list.drugs"
                :key="index"
                class="mt-4"
              >
                <b-col cols="4">
                  <PictureFallback
                    type="product"
                    :picture="drug.picture"
                    class="border"
                  />
                </b-col>
                <b-col>
                  <div class="drug-name">{{ drug.description }}</div>
                  <div>
                    {{ $t("checkout_control.quantity") }}: {{ drug.quantity }}
                    {{ getPackDescription(drug) }}
                  </div>
                  <div>
                    {{ $t("checkout_control.unit_price") }}:
                    {{
                      $formatMoney(
                        Number(drug.price.no_tax_price) +
                          Number(drug.price.discounted),
                        drug.price.no_tax_price_currency
                      )
                    }}
                  </div>
                  <div
                    v-if="
                      drug.price.discounts && drug.price.discounts.length > 0
                    "
                  >
                    {{ $t("checkout_control.discount_price") }}:
                    <b-badge
                      v-for="(discount, index) in drug.price.discounts"
                      :key="index"
                      variant="warning"
                    >
                      {{ Number(discount.percent_value) }}%
                    </b-badge>
                    :
                    {{
                      $formatMoney(drug.price.no_tax_price, drug.price.currency)
                    }}
                  </div>
                  <div v-if="drug.price.tax">
                    {{ $t("checkout_control.taxes") }}:
                    {{ $formatMoney(drug.price.tax, drug.price.currency) }}
                  </div>
                  <div class="drug-total">
                    {{ $t("checkout_control.total") }}:
                    {{
                      $formatMoney(
                        drug.price.all_included_price * drug.quantity,
                        drug.price.currency
                      )
                    }}
                  </div>
                  <!-- Este producto en concreto necesita receta si está en la fracción indicada o si la dirección de
                        envío se encuentra fuera de MX, en cuyo caso todos los medicamentos necesitan receta -->
                  <div v-if="['I', 'II', 'III', 'IV'].includes(drug.fraction) || delivery_address.country != 'MX'">
                    <font-awesome-icon
                      v-b-tooltip.hover
                      class="mr-2"
                      icon="info-circle"
                    />
                    <small>{{ $t("checkout_control.recipe_required") }}</small>
                  </div>
                </b-col>
              </b-row>
              <b-row v-if="handle_drug_recipe_file && isRecipeRequired(transaction.item_list.drugs)">
                <b-col cols="12">
                  <div class="pt-5">
                    <div v-if="getDocumentsByOrganizationUUID(transaction.organization_uuid).length <= 0">
                      <font-awesome-icon
                        v-b-tooltip.hover
                        class="mr-2"
                        icon="exclamation-triangle"
                      />
                      <small>{{ $t("checkout_control.recipe_needed_warning") }}</small>
                    </div>
                    <div
                      class="pt-2"
                      v-for="(document, index) in getDocumentsByOrganizationUUID(transaction.organization_uuid)"
                      :key="index"
                    >
                      <VTransactionFileForm
                        :file_uploaded="document"
                        :file_label="$t('checkout_control.file_recipe_label')"
                        :file_type="22"
                        :disabled="order_done"
                        :organization_uuid="transaction.organization_uuid"
                        :documentation_uuid="documentation_uuid"
                        :file_browse_text="$t('checkout_control.file_recipe_browse_text')"
                        file_send_dispatch="checkout/SEND_CART_DOCUMENT"
                        file_delete_dispatch="checkout/DELETE_CART_DOCUMENT"
                      />
                    </div>
                    <div class="pt-2">
                      <VTransactionFileForm
                        v-if="!order_done"
                        :file_label="$t('checkout_control.file_recipe_label')"
                        :file_type="22"
                        :organization_uuid="transaction.organization_uuid"
                        :documentation_uuid="documentation_uuid"
                        :file_browse_text="$t('checkout_control.file_recipe_browse_text')"
                        file_send_dispatch="checkout/SEND_CART_DOCUMENT"
                        file_delete_dispatch="checkout/DELETE_CART_DOCUMENT"
                      />
                    </div>
                  </div>
                </b-col>
              </b-row>
              <b-row v-if="selectedCarrier">
                <b-col>
                  <div class="shipping-title pt-5">
                    {{ $t("checkout_control.shipping_method_title") }}
                  </div>
                  <b-row class="mt-2">
                    <b-col cols="4">
                      <PictureFallback
                        type="carrier"
                        :src="selectedCarrier.carrier_logo"
                        class="border"
                      />
                    </b-col>
                    <b-col>
                      <div class="drug-name">
                        {{ selectedCarrier.carrier_name }}
                      </div>
                      <div>
                        {{ selectedCarrier.external_service_product_name }}
                      </div>
                      <b-link v-if="!order_done" to @click="openModalDelivery">
                        {{ $t("checkout_control.change") }}
                      </b-link>
                    </b-col>
                  </b-row>
                  <b-row v-if="selectedCarrier.cross_customs">
                    <div>
                      <font-awesome-icon
                        v-b-tooltip.hover
                        class="mr-2"
                        icon="exclamation-triangle"
                      />
                      <small>
                        {{ $t("checkout_control.customs_warning") }}
                      </small>
                    </div>
                  </b-row>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <TotalAmountSummary
                    :showAll="true"
                    :amount="amount"
                    showShipping
                    showDiscount
                    showTotal
                    borderless
                    striped
                    small
                    :order="transaction"
                    class="py-5"
                  />
                </b-col>
              </b-row>
            </b-col>
            <!-- Si se necesita receta, bloquear hasta que se suba al menos un archivo -->
            <b-col cols="12" md="3" v-if="handle_drug_recipe_file && isRecipeRequired(transaction.item_list.drugs) && getDocumentsByOrganizationUUID(transaction.organization_uuid).length <= 0">
              <font-awesome-icon
                v-b-tooltip.hover
                class="mr-2"
                icon="exclamation-triangle"
              />
              <small>{{ $t("checkout_control.recipe_needed_warning") }}</small>
            </b-col>
            <b-col cols="12" md="3" v-else>
              <b-alert
                v-if="!purchase_options.carriers_delivery.length"
                show
                variant="danger"
              >
                {{ $t("checkout_control.no_carrier_delivery_available") }}
              </b-alert>
              <b-alert
                v-else-if="!purchase_options.payments_configurations.length"
                show
                variant="danger"
              >
                {{ $t("checkout_control.no_payment_method_available") }}
              </b-alert>
              <b-button
                v-else-if="!selectedCarrier"
                variant="primary"
                @click="openModalDelivery"
              >
                {{ $t("checkout_control.delivery") }}
              </b-button>
              <div v-else-if="!order_done">
                <b-button variant="primary" @click="openModalPayment">{{
                  $t("checkout_control.pay")
                }}</b-button>
                <b-alert :show="payment_fail" variant="danger" class="mt-2">{{
                  $t("checkout_control.payment_fail")
                }}</b-alert>
              </div>
              <div v-else>
                <div class="paid text-uppercase">
                  <div v-if="order.payment.payed">
                    {{ $t("checkout_control.paid") }}
                  </div>
                  <div class="pending" v-else>
                    {{ $t("checkout_control.pending_payment") }}
                  </div>
                </div>
                <b-link :to="routeToDetails">{{
                  $t("checkout_control.cart_done_review_order")
                }}</b-link>
              </div>
            </b-col>
          </b-row>
        </b-container>

        <b-modal
          :ref="'modal-delivery-' + transaction.organization_uuid"
          centered
          size="lg"
          :title="$t('checkout_control.select_delivery_method')"
          hide-footer
        >
          <CheckoutControlDelivery
            v-if="purchase_options.carriers_delivery.length"
            :options="purchase_options.carriers_delivery"
            @input="handleDeliveryChange"
          />
        </b-modal>

        <b-modal
          :ref="'modal-payment-' + transaction.organization_uuid"
          centered
          :title="$t('checkout_control.select_payment_method')"
          hide-footer
          no-close-on-esc
          no-close-on-backdrop
        >
          <!-- El objeto amount siempre debe tener el total actualizado, incluso
        si se cambia el transportista (ya que se hace una nueva petición del
        carrito a la api, lo que se trae los costes actualizados) -->
          <CheckoutControlPayment
            v-if="
              selectedCarrier && purchase_options.payments_configurations.length
            "
            :amount="amount"
            :options="purchase_options.payments_configurations"
            :pharmacy_uuid="transaction.organization_uuid"
            :checkout_uuid="checkout_uuid"
            v-on:payment-authorized="onPaymentAuthorized"
            v-on:payment-intention="onPaymentIntention"
            :webpay_pay_errors.sync="webpay_pay_errors"
          />
        </b-modal>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import get from "lodash/get";
import { Money } from "@/lib/money.js";
import { mapActions, mapGetters, mapState } from "vuex";
import { isDrugsPack, getPackQuantity } from "@/lib/drugs.js";

import CheckoutControlDelivery from "@/components/CheckoutControlDelivery";
import CheckoutControlPayment from "@/components/CheckoutControlPayment";
import VTransactionFileForm from "@/components/VTransactionFileForm";
import PictureFallback from "@/components/PictureFallback";
import TotalAmountSummary from "@/components/TotalAmountSummary";
import { Amount } from "@/lib/amount.js";

export default {
  name: "checkout-control",
  components: {
    CheckoutControlDelivery,
    CheckoutControlPayment,
    VTransactionFileForm,
    PictureFallback,
    TotalAmountSummary,
  },
  props: {
    transaction: {
      type: Object,
      default: undefined,
    },
    checkout_uuid: {
      type: String,
      default: undefined,
    },
    has_payment_intention: {
      type: Boolean,
      default: false,
    },
    // Para procesos de checkout donde se requiere documentación
    // extra relacionada con los productos
    handle_drug_recipe_file: {
      type: Boolean,
      default: false,
    },
    documentation_uuid: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      order_done: false,
      payment_fail: false,
      finish_pay_loading: false,
      order_finish_loading: undefined,
      order: undefined,
      webpay_pay_errors: [],
    };
  },
  computed: {
    ...mapGetters("checkout", ["getDocumentsByOrganizationUUID"]),
    ...mapState({
      delivery_address: (state) => state.checkout.delivery_address,
    }),
    amount() {
      return new Amount(this.transaction.amount);
    },
    selectedCarrier() {
      if (this.transaction.carrier_delivery_snapshot.uuid) {
        return this.transaction.carrier_delivery_snapshot;
      }
      return undefined;
    },
    purchase_options() {
      if (!this.transaction.purchase_options) {
        return undefined;
      }
      return this.transaction.purchase_options;
    },
    routeToDetails() {
      return {
        name: "order-details",
        params: {
          order_uuid: this.order.uuid,
        },
      };
    },
    order_payment_transaction() {
      if (!this.order) {
        return undefined;
      }
      let transactions = get(this.order, "payment.transactions");
      if (!transactions) {
        return undefined;
      }
      if (!transactions[0]) {
        return undefined;
      }
      return transactions[0];
    },
    order_auth_code() {
      if (!this.order_payment_transaction) {
        return undefined;
      }
      let auth_code = get(
        this.order_payment_transaction,
        "intention.service_auth_code"
      );
      if (!auth_code) {
        return undefined;
      }
      return auth_code;
    },
    order_payment_date() {
      if (!this.order_payment_transaction) {
        return undefined;
      }
      return this.order_payment_transaction.payment_date;
    },
    order_payment_intention_uuid() {
      if (!this.order_payment_transaction) {
        return undefined;
      }
      let intention_uuid = get(
        this.order_payment_transaction,
        "intention.uuid"
      );
      if (!intention_uuid) {
        return undefined;
      }
      return intention_uuid;
    },
    order_payment_amount() {
      if (!this.order_payment_transaction) {
        return undefined;
      }
      let amount = this.order_payment_transaction.total;
      let currency = this.order_payment_transaction.total_currency;
      let total_money = new Money(amount, currency);
      return total_money.toLocaleString();
    },
    is_order_payed_and_with_webpay() {
      if (this.order && this.order.payment && this.order.payment.payed) {
        if (this.order_payment_transaction.payment_method.payment_method == 9) {
          return true;
        }
      }
      return false;
    },
  },
  methods: {
    ...mapActions({
      CHECKOUT_TO_RETAIL_ORDER: "checkout/CHECKOUT_TO_RETAIL_ORDER",
    }),
    isRecipeRequired(drugs) {
      const even = (element) =>
        ["I", "II", "III", "IV"].includes(element.fraction);
      return drugs.some(even) || this.delivery_address.country != "MX";
    },
    onPaymentIntention(data) {
      this.$emit(
        "payment-intention",
        Object.assign({}, data, {
          pharmacy_uuid: this.transaction.organization_uuid,
          organization_uuid: this.transaction.organization_uuid,
        })
      );
    },
    onPaymentAuthorized(data) {
      this.$refs["modal-payment-" + this.transaction.organization_uuid].hide();
      this.$emit(
        "payment-authorized",
        Object.assign({}, data, {
          pharmacy_uuid: this.transaction.organization_uuid,
          organization_uuid: this.transaction.organization_uuid,
        })
      );
      var payload = {
        checkout_uuid: this.checkout_uuid,
        data: {
          pharmacy: { uuid: this.transaction.organization_uuid },
          payment_configuration: {
            uuid: data.payment_conf.uuid,
            payment_id: data.payment_conf.payment_id,
            payer_id: data.payment_conf.payer_id,
          },
          payment_intention_uuid: data.payment_intention_uuid,
        },
      };
      this.order_finish_loading = true;
      this.CHECKOUT_TO_RETAIL_ORDER(payload).then(
        (response) => {
          this.order = response.data.order;
          this.order_done = true;
          this.payment_fail = false;
          this.order_finish_loading = false;
        },
        (err) => {
          this.order_finish_loading = false;
          this.payment_fail = true;
          this.$sentry({ capture: err });
          this.mnotify_error({
            text: this.$t("checkout_control.error.cannot_pay"),
          });
        }
      );
    },
    handleDeliveryChange: function (_delivery) {
      // cuando se cambia el carrier de la farmacia representada en este
      // componenete, lanzamos un evento al padre. Éste se encargará de
      // recargar los datos de amount y lo que haga falta.
      this.$refs["modal-delivery-" + this.transaction.organization_uuid].hide();
      var delivery = Object.assign({}, _delivery);
      delivery.pharmacy_uuid = this.transaction.organization_uuid;
      this.$emit("carrier-select", delivery);
    },
    openModalDelivery() {
      this.$refs["modal-delivery-" + this.transaction.organization_uuid].show();
    },
    openModalPayment() {
      this.webpay_pay_errors = [];
      this.$refs["modal-payment-" + this.transaction.organization_uuid].show();
    },
    getPackDescription(drug) {
      if (isDrugsPack(drug)) {
        return this.$t("checkout_control.offer_pack", {
          quantity: getPackQuantity(drug),
        });
      }
      return undefined;
    },
  },
  fallbackI18n: {
    messages: {
      und: {
        checkout_control: {
          payment_in_progress: "Payment in progress...",
          please_wait: "Please wait...",
          cart_done_title: "Congratulations!",
          cart_done_subtitle: "Yor order has been registered",
          cart_done_review_order: "Review your order",
          customs_warning:
            "The delivery of this product is subject to customs processes. The shipment does not include costs related to its customs clearance in the destination country",
          retry_button: "Retry",
          payment_fail:
            "Sorry, the checkout process has failed, please, try again later",
          sub_summary_title: "Summary",
          subtotal: "Subtotal",
          total: "Total",
          shipping_method_title: "Shipping method",
          pay_title: "Checkout",
          no_payment_method_available:
            "Sorry, this pharmacy doesn't have any payment method for the selected shipping address",
          no_carrier_delivery_available:
            "Sorry, this pharmacy doesn't have any delivery method for the selected shipping address",
          error: {
            set_delivery_carrier:
              "Cannot set delivery carrier for this pharmacy",
            cannot_pay: "The payment could not be made",
          },
          webpay_payment_confirm: "Your payment has been accepted",
          webpay_payment_auth_info: "Bank authorization number: {auth_code}",
          webpay_payment_date: "Payment date: {payment_date}",
          webpay_payment_reference: "Payment reference: {payment_reference}",
          webpay_payment_amount: "Payed amount: {payment_amount}",
          quantity: "Quantity",
          unit_price: "Unit price",
          pay: "Pay",
          delivery: "Delivery",
          select_delivery_method: "Select delivery method",
          change: "Change",
          select_payment_method: "Select payment method",
          paid: "Paid",
          taxes: "Taxes",
          pending_payment: "Pending payment",
          discount_price: "Discount price",
          offer_pack: "Offer Pack X{quantity}",
          file_recipe_label: "Recipe",
          file_recipe_browse_text: "Upload Recipe",
          recipe_needed_warning:
            "Some of your products require a recipe. Please upload your recipe to continue ordering.",
          recipe_required: "This product needs a recipe",
        },
      },
    },
  },
};
</script>

<style scoped lang="scss">
.pharmacy-logo-box {
  display: flex;
  vertical-align: middle;

  & img {
    max-width: 165px;
    max-height: 165px;
  }
}

.drug-name {
  font-size: $font-size-default * 1.1;
  font-weight: bold;
}

.drug-total {
  font-weight: bold;
}

.summary-title {
  font-size: $font-size-medium * 1.2;
  font-weight: bold;
}

.shipping-title {
  font-size: $font-size-default * 1.1;
  font-weight: bold;
}

.paid {
  color: $corp-color2;
  font-size: $font-size-medium * 1.1;
  font-weight: bold;
}
.pending {
  color: $corp-color1;
}
.behind_modal_z_index {
  z-index: 999 !important;
}
</style>
